import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  BarChart3, 
  Users, 
  MousePointerClick, 
  TrendingUp,
  Globe,
  FileText,
  Eye,
  ArrowRight,
  RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AnalyticsDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [leadSources, setLeadSources] = useState([]);
  const [pagePerformance, setPagePerformance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(30);

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const [summaryRes, sourcesRes, pagesRes] = await Promise.all([
        axios.get(`${API}/analytics/summary?days=${timeRange}`),
        axios.get(`${API}/analytics/sources?days=${timeRange}`),
        axios.get(`${API}/analytics/pages?days=${timeRange}`)
      ]);
      setSummary(summaryRes.data);
      setLeadSources(sourcesRes.data);
      setPagePerformance(pagesRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatPageName = (page) => {
    if (!page) return 'Unknown';
    const pageNames = {
      '/': 'Home',
      '/romania': 'Romania',
      '/english': 'USA/English',
      '/espanol': 'Spanish',
      '/hindi': 'India/Hindi',
      '/hrvatski': 'Croatian',
      '/hindi-form': 'Hindi Signup Form'
    };
    return pageNames[page] || page;
  };

  const formatSourceName = (source) => {
    if (!source) return 'Direct';
    const sourceNames = {
      'direct': 'Direct Traffic',
      'google': 'Google',
      'facebook': 'Facebook',
      'instagram': 'Instagram',
      'twitter': 'Twitter/X',
      'linkedin': 'LinkedIn',
      'youtube': 'YouTube',
      'referral': 'Referral',
      'contact_form': 'Contact Form',
      'romania-signup': 'Romania Signup',
      'english-signup': 'English Signup',
      'espanol-signup': 'Spanish Signup',
      'hrvatski-signup': 'Croatian Signup',
      'india-signup': 'India Signup'
    };
    return sourceNames[source] || source.replace(/-/g, ' ').replace(/_/g, ' ');
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <RefreshCw className="animate-spin" size={32} style={{ color: '#D4AF37' }} />
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem 0' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#D4AF37', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <BarChart3 size={24} />
          Analytics Dashboard
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {[7, 30, 90].map(days => (
            <Button
              key={days}
              onClick={() => setTimeRange(days)}
              style={{
                background: timeRange === days ? '#D4AF37' : 'rgba(255,255,255,0.1)',
                color: timeRange === days ? '#000' : '#fff',
                padding: '0.5rem 1rem',
                fontSize: '0.875rem'
              }}
            >
              {days}d
            </Button>
          ))}
          <Button onClick={fetchAnalytics} style={{ background: 'rgba(255,255,255,0.1)', padding: '0.5rem' }}>
            <RefreshCw size={16} />
          </Button>
        </div>
      </div>

      {/* Overview Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        <Card style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
          <CardContent style={{ padding: '1.25rem', textAlign: 'center' }}>
            <Eye size={24} style={{ color: '#3B82F6', marginBottom: '0.5rem' }} />
            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#3B82F6' }}>
              {summary?.total_page_views || 0}
            </div>
            <div style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)' }}>Page Views</div>
          </CardContent>
        </Card>

        <Card style={{ background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
          <CardContent style={{ padding: '1.25rem', textAlign: 'center' }}>
            <Users size={24} style={{ color: '#10B981', marginBottom: '0.5rem' }} />
            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#10B981' }}>
              {summary?.total_unique_visitors || 0}
            </div>
            <div style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)' }}>Unique Visitors</div>
          </CardContent>
        </Card>

        <Card style={{ background: 'rgba(212, 175, 55, 0.1)', border: '1px solid rgba(212, 175, 55, 0.3)' }}>
          <CardContent style={{ padding: '1.25rem', textAlign: 'center' }}>
            <FileText size={24} style={{ color: '#D4AF37', marginBottom: '0.5rem' }} />
            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#D4AF37' }}>
              {summary?.total_form_submissions || 0}
            </div>
            <div style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)' }}>Form Submissions</div>
          </CardContent>
        </Card>

        <Card style={{ background: 'rgba(139, 92, 246, 0.1)', border: '1px solid rgba(139, 92, 246, 0.3)' }}>
          <CardContent style={{ padding: '1.25rem', textAlign: 'center' }}>
            <TrendingUp size={24} style={{ color: '#8B5CF6', marginBottom: '0.5rem' }} />
            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#8B5CF6' }}>
              {summary?.conversion_rate || 0}%
            </div>
            <div style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)' }}>Conversion Rate</div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
        <Card style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <CardContent style={{ padding: '1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#fff' }}>{summary?.views_today || 0}</div>
            <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>Views Today</div>
          </CardContent>
        </Card>
        <Card style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <CardContent style={{ padding: '1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#fff' }}>{summary?.views_this_week || 0}</div>
            <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>Views This Week</div>
          </CardContent>
        </Card>
        <Card style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <CardContent style={{ padding: '1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#fff' }}>{summary?.views_this_month || 0}</div>
            <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>Views This Month</div>
          </CardContent>
        </Card>
      </div>

      {/* Funnel */}
      {summary?.funnel_stats && summary.funnel_stats.length > 0 && (
        <Card style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', marginBottom: '2rem' }}>
          <CardHeader>
            <CardTitle style={{ color: '#D4AF37', fontSize: '1.125rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <TrendingUp size={20} />
              Conversion Funnel
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              {summary.funnel_stats.map((stage, index) => (
                <React.Fragment key={stage.stage}>
                  <div style={{ textAlign: 'center', minWidth: '120px' }}>
                    <div style={{ 
                      fontSize: '1.75rem', 
                      fontWeight: '700', 
                      color: index === 0 ? '#3B82F6' : index === 1 ? '#D4AF37' : '#10B981'
                    }}>
                      {stage.count}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)' }}>{stage.stage}</div>
                    <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>{stage.percentage}%</div>
                  </div>
                  {index < summary.funnel_stats.length - 1 && (
                    <ArrowRight size={24} style={{ color: 'rgba(255,255,255,0.3)' }} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1.5rem' }}>
        {/* Lead Sources */}
        <Card style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <CardHeader>
            <CardTitle style={{ color: '#D4AF37', fontSize: '1.125rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Globe size={20} />
              Where Leads Come From
            </CardTitle>
          </CardHeader>
          <CardContent>
            {leadSources.length === 0 ? (
              <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', padding: '2rem' }}>
                No lead data yet
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {leadSources.slice(0, 10).map((source, index) => (
                  <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{ 
                        width: '8px', 
                        height: '8px', 
                        borderRadius: '50%', 
                        background: ['#3B82F6', '#10B981', '#D4AF37', '#8B5CF6', '#EF4444', '#F59E0B'][index % 6]
                      }} />
                      <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem' }}>
                        {formatSourceName(source.source_detail || source.source)}
                      </span>
                    </div>
                    <span style={{ 
                      color: '#D4AF37', 
                      fontWeight: '600',
                      background: 'rgba(212, 175, 55, 0.1)',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '12px',
                      fontSize: '0.875rem'
                    }}>
                      {source.leads} leads
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Page Performance */}
        <Card style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <CardHeader>
            <CardTitle style={{ color: '#D4AF37', fontSize: '1.125rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <MousePointerClick size={20} />
              Page Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pagePerformance.length === 0 && (!summary?.page_views_by_page || summary.page_views_by_page.length === 0) ? (
              <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', padding: '2rem' }}>
                No page view data yet. Views will appear as users visit your site.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {(pagePerformance.length > 0 ? pagePerformance : summary?.page_views_by_page || []).slice(0, 8).map((page, index) => (
                  <div key={index} style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    padding: '0.5rem',
                    background: 'rgba(255,255,255,0.03)',
                    borderRadius: '8px'
                  }}>
                    <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem' }}>
                      {formatPageName(page.page)}
                    </span>
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                      <span style={{ color: '#3B82F6', fontSize: '0.875rem' }}>
                        {page.views || 0} views
                      </span>
                      {(page.form_submits > 0 || page.conversion_rate > 0) && (
                        <span style={{ color: '#10B981', fontSize: '0.875rem' }}>
                          {page.conversion_rate || 0}% conv
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Traffic Sources from Analytics */}
      {summary?.traffic_sources && summary.traffic_sources.length > 0 && (
        <Card style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', marginTop: '1.5rem' }}>
          <CardHeader>
            <CardTitle style={{ color: '#D4AF37', fontSize: '1.125rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <TrendingUp size={20} />
              Traffic Sources
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                    <th style={{ textAlign: 'left', padding: '0.75rem', color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem' }}>Source</th>
                    <th style={{ textAlign: 'right', padding: '0.75rem', color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem' }}>Visits</th>
                    <th style={{ textAlign: 'right', padding: '0.75rem', color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem' }}>Conversions</th>
                    <th style={{ textAlign: 'right', padding: '0.75rem', color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem' }}>Conv. Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.traffic_sources.map((source, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <td style={{ padding: '0.75rem', color: '#fff', fontSize: '0.875rem' }}>
                        {formatSourceName(source.source)}
                      </td>
                      <td style={{ textAlign: 'right', padding: '0.75rem', color: '#3B82F6', fontSize: '0.875rem' }}>
                        {source.visits}
                      </td>
                      <td style={{ textAlign: 'right', padding: '0.75rem', color: '#D4AF37', fontSize: '0.875rem' }}>
                        {source.conversions}
                      </td>
                      <td style={{ textAlign: 'right', padding: '0.75rem', color: '#10B981', fontSize: '0.875rem' }}>
                        {source.conversion_rate}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
